import React from 'react';
import cx from 'classnames';
import { renderToString } from 'react-dom/server';
import { isEmpty, isNil, last } from 'lodash-es';

import lineChartConfig from '../../../../components/Chart/LineChart';
import { format, formatQuarterYear } from '../../../../utils/date';
import { getFallbackSubDateByGranularity } from '../../../../utils/helpers';
import { numberToHumanChart } from '../../../../utils/formatter';

const onNextTick = (callback, timeout = 0) => setTimeout(callback, timeout);

const xLabelFormat = (value, granularity = 'quarterly') => {
  switch (granularity) {
    case 'quarterly':
      return formatQuarterYear(value);
    case 'monthly':
      return format(value, 'MMM ‘YY');
    case 'weekly':
      return format(value, 'MMM DD, ‘YY');
    default:
      return format(value, 'MMM DD, ‘YY');
  }
};

export const getComparisonDatePair = (datePair, {
  currentTimestamp,
  overPeriod,
  granularity = 'quarterly',
}) => {
  if (!isEmpty(datePair)) {
    return datePair;
  }

  // Fallback
  const subDate = getFallbackSubDateByGranularity({
    isOverPeriod: overPeriod === 'period_over_period',
    granularity,
    date: currentTimestamp,
  });

  return {
    currDate: formatQuarterYear(currentTimestamp),
    prevDate: formatQuarterYear(subDate),
  };
};

export const baseDriversConfig = (seriesByMetric, {
  chartData,
  overPeriod,
  showActivePoint = false,
  isIntraPeriod = false,
  granularity,
  showEdgeLabels,
  chartStyleOverride,
}) => {
  const animation = chartStyleOverride?.animation ?? {
    duration: 300,
  };

  const { partial_days, sub_dates_grouped, start_partial_days, end_partial_days } = chartData;

  const startPartialDate = 1;
  const tailEndPartialDate = (isIntraPeriod ? end_partial_days : partial_days) || startPartialDate;
  const headEndPartialDate = (isIntraPeriod && start_partial_days) || startPartialDate;

  let xTickPositions = seriesByMetric.map(value => value[0]);
  const tickAmountLimit = 10;
  if (isIntraPeriod) {
    if (showEdgeLabels) {
      xTickPositions = seriesByMetric.filter((_, index) => (
        index === 0 || index === seriesByMetric.length - 1)).map(item => item[0]);
    } else if (seriesByMetric.length > tickAmountLimit) {
      const step = Math.ceil(seriesByMetric.length / tickAmountLimit);
      xTickPositions = seriesByMetric.filter((_, index) => index % step === 0).map(item => item[0]);
    }
  }

  return lineChartConfig({
    chartOptions: {
      type: isIntraPeriod ? 'spline' : 'column',
      spacing: [10, 0, 8, 0],
      height: chartStyleOverride?.chartHeight || 245,
      animation,
    },
    yAxis: {
      title: null,
      tickAmount: 6,
      startOnTick: true,
      labels: {
        reserveSpace: true,
        y: 5,
        style: {
          color: '#848EA1',
          fontSize: chartStyleOverride?.axisLabelsFontSize || 12,
        },
        formatter() {
          return isIntraPeriod ? `${numberToHumanChart(this.value)}` : `${this.value}%`;
        },
      },
      gridLineWidth: 1,
      gridLineColor: '#EDF0F5',
    },
    xAxisOptions: {
      tickLength: 0,
      lineWidth: 0,
      crosshair: false,
      minPadding: 0,
      tickPositions: xTickPositions.length > 0 ? xTickPositions : null,
    },
    xAxisLabels: {
      formatter() {
        if (isIntraPeriod) {
          return xTickPositions.includes(this.value)
            ? (sub_dates_grouped?.[this.value]?.currDate || xLabelFormat(this.value, granularity))
            : '';
        }

        if (!showEdgeLabels || this.isFirst || this.isLast) {
          return `<span style="${showActivePoint && this.isLast && 'color: #348BE3; font-weight: 700;'}">${sub_dates_grouped?.[this.value]?.currDate || xLabelFormat(this.value, granularity)}</span>`;
        }
        return '';
      },
      style: {
        color: '#848EA1',
        fontSize: 12,
      },
      overflow: 'justify',
      showFirstLabel: true,
      y: 18,
      useHTML: true,
    },
    tooltipOptions: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderRadius: 10,
      padding: 0,
      useHTML: true,
      outside: true,
      formatter() {
        const pointOptions = this.points[0].point.options;
        const isPartialElem = pointOptions.isPartial;
        const endPartialDate = (pointOptions.isStartPartial && !pointOptions.isEndPartial)
          ? headEndPartialDate
          : tailEndPartialDate;

        const renderTooltipContent = rowsContent => renderToString(
          <div
            style={{
              minWidth: '170px',
              borderRadius: '8px',
              border: '1px solid #E1E5EB',
              padding: '12px 16px',
              backgroundColor: '#fff',
              boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.08)',
            }}
          >
            <table className="table">
              {isPartialElem && (
                <tr>
                  <td className="text-left text-black text-12 p-l-0 p-b-0 pr-md">
                    Days {endPartialDate === 0 ? `${startPartialDate}` : `${startPartialDate} - ${endPartialDate}`}
                  </td>
                </tr>
              )}
              {rowsContent}
            </table>
          </div>,
        );

        if (isIntraPeriod) {
          return renderTooltipContent(
            <tr>
              <td className="text-left text-black text-12 p-l-0 p-b-0 pr-md">
                {sub_dates_grouped?.[this.x]?.currDate || xLabelFormat(this.x, granularity)}
              </td>
              <td className="text-right text-12 p-l-0 p-b-0" style={{ color: '#5D6B85', lineHeight: '150%' }}>
                <div className="text-bold text-black text-16">
                  <span>{numberToHumanChart(this.y)}</span>
                </div>
              </td>
            </tr>,
          );
        }

        const { currDate, prevDate } = getComparisonDatePair(sub_dates_grouped?.[this.x], {
          currentTimestamp: this.x,
          overPeriod,
          granularity,
        });

        return renderTooltipContent(
          <tr>
            <td className="text-left text-black text-12 p-l-0 p-b-0 pr-md">
              {currDate} vs {prevDate}
            </td>
            <td className="text-right text-12 p-l-0 p-b-0" style={{ color: '#5D6B85', lineHeight: '150%' }}>
              <div className="text-bold text-black text-16">
                <span>{this.y}%</span>
              </div>
            </td>
          </tr>,
        );
      },
    },
    plotOptionsSeries: {
      animation,
      turboThreshold: 2500,
    },
    plotOptionsSettings: {
      column: {
        minPointLength: 5,
        groupPadding: 0.05,
        borderRadius: 0,
      },
    },
  });
};

export const advancedColumnDriversConfig = (seriesByMetric, {
  chartData,
  quarterDatesByTimestamp,
  metric,
  activeTimestamp,
  overPeriod,
  granularity,
  showEdgeLabels,
  chartStyleOverride,
  renderActiveRect,
  renderHoverRect,
  renderMaskRects,
  onPointSelect,
}) => {
  const animation = chartStyleOverride?.animation ?? {
    duration: 300,
  };

  const { partial_days, sub_dates_grouped } = chartData;

  const startPartialDate = 1;
  const endPartialDate = partial_days || startPartialDate;

  const xTickPositions = seriesByMetric.map(value => value[0]);

  const getRectParams = ({
    plotHeight,
    plotTop,
    plotLeft,
    points,
    targetPointX,
  }) => {
    const getRectParamsByPoint = ({ pointWidth, barX }) => {
      const padding = 7;
      const plotBandsWidth = pointWidth + 2 * padding;
      const left = (barX + plotLeft) - padding;

      return {
        height: plotHeight,
        width: plotBandsWidth,
        top: plotTop,
        left,
      };
    };

    if (points) {
      if (isNil(targetPointX)) {
        return points.map(point => getRectParamsByPoint(point));
      }

      const activePoint = points.find(point => point.x === targetPointX) || last(points);

      if (activePoint) {
        return getRectParamsByPoint(activePoint);
      }
    }

    return null;
  };

  let destroyHoverRect;
  let activeRectLeftCoord;

  const clearHoverRect = () => {
    if (destroyHoverRect) {
      destroyHoverRect();
      destroyHoverRect = undefined;
    }
  };

  return lineChartConfig({
    chartOptions: {
      type: 'column',
      spacing: [10, 0, 8, 0],
      height: chartStyleOverride?.chartHeight || 245,
      animation,
      events: {
        load() {
          // Reset coords
          activeRectLeftCoord = undefined;
        },
        render(event) {
          const { plotHeight, plotTop, plotLeft, series } = event.target;
          const { points } = series[0] || {};

          if (!points) return;
          const chartRenderer = this.renderer;

          // Render active rect
          const activeRectParams = getRectParams({
            plotHeight,
            plotTop,
            plotLeft,
            points,
            targetPointX: activeTimestamp,
          });

          // Is active point changed
          if (activeRectParams && (activeRectParams.left !== activeRectLeftCoord)) {
            activeRectLeftCoord = activeRectParams.left;
            renderActiveRect(chartRenderer, [activeRectParams]);

            // Render mask rects
            const maskRectsParams = getRectParams({
              plotHeight,
              plotTop,
              plotLeft,
              points,
            });

            renderMaskRects(chartRenderer, maskRectsParams, points.map((point, index) => ({
              className: cx('rect-mask-overlay', point.isActivePoint && 'active'),
              onClick: () => {
                clearHoverRect();

                if (onPointSelect && !point.isActivePoint) {
                  onPointSelect({
                    qLabel: quarterDatesByTimestamp[point.x],
                    metric,
                    index,
                  });
                }
              },
              mouseOver() {
                if (point.isActivePoint) {
                  clearHoverRect();
                  return;
                }
                const rectParams = maskRectsParams[index];

                if (!rectParams) return;

                destroyHoverRect = renderHoverRect(chartRenderer, [rectParams]);
              },
              mouseOut() {
                if (point.isActivePoint) return;
                clearHoverRect();
              },
            })));
          }
        },
      },
    },
    yAxis: {
      title: null,
      tickAmount: 5,
      startOnTick: true,
      labels: {
        reserveSpace: true,
        y: 5,
        style: {
          color: '#848EA1',
          fontSize: 12,
        },
        formatter() {
          return `${this.value}%`;
        },
      },
      gridLineWidth: 1,
      gridLineColor: '#EDF0F5',
    },
    xAxisOptions: {
      tickLength: 0,
      lineWidth: 0,
      crosshair: false,
      minPadding: 0,
      tickPositions: xTickPositions.length > 0 ? xTickPositions : null,
    },
    xAxisLabels: {
      formatter() {
        if (!showEdgeLabels || (this.isFirst || this.isLast || this.value === activeTimestamp)) {
          return `<span class="text-nowrap" style="${this.value === activeTimestamp && 'color: #348BE3; font-weight: 700;'}">${sub_dates_grouped?.[this.value]?.currDate || xLabelFormat(this.value, granularity)}</span>`;
        }
        return '';
      },
      style: {
        color: '#848EA1',
        fontSize: 12,
      },
      showFirstLabel: true,
      y: 18,
      zIndex: 2,
      useHTML: true,
    },
    tooltipOptions: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderRadius: 10,
      padding: 0,
      useHTML: true,
      outside: true,
      formatter() {
        const isPartialElem = this.points[0].point.options.isPartial;

        const { currDate, prevDate } = getComparisonDatePair(sub_dates_grouped?.[this.x], {
          currentTimestamp: this.x,
          overPeriod,
          granularity,
        });

        return renderToString(
          <div
            style={{
              minWidth: '170px',
              borderRadius: '8px',
              border: '1px solid #E1E5EB',
              padding: '12px 16px',
              backgroundColor: '#fff',
              boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.08)',
            }}
          >
            <table className="table">
              <tr style={{ display: !isPartialElem && 'none' }}>
                <td className="text-left text-black text-12 p-l-0 p-b-0 pr-md">
                  Days {endPartialDate === 0 ? `${startPartialDate}` : `${startPartialDate} - ${endPartialDate}`}
                </td>
              </tr>
              <tr>
                <td className="text-left text-black text-12 p-l-0 p-b-0 pr-md">
                  {currDate} vs {prevDate}
                </td>
                <td className="text-right  text-12 p-l-0 p-b-0" style={{ color: '#5D6B85', lineHeight: '150%' }}>
                  <div className="text-bold text-black text-16">
                    <span>{this.y}%</span>
                  </div>
                </td>
              </tr>
            </table>
          </div>,
        );
      },
    },
    plotOptionsSeries: {
      animation,
    },
    plotOptionsSettings: {
      column: {
        minPointLength: 5,
        groupPadding: 0.1,
        borderRadius: 0,
        borderWidth: 0,
      },
    },
  });
};

const decorateYValue = (value, postfix = '%') => value && `${numberToHumanChart(value)}${postfix}`;

export const advancedLineDriversConfig = ({
  floor,
  ceiling,
  labelStep,
  metric,
  showNoDataMessage,
  showEdgeLabels,
  chartStyleOverride,
  setHintCoords,
}) => {
  const animation = chartStyleOverride?.animation ?? {
    duration: 300,
  };

  return lineChartConfig({
    chartOptions: {
      type: 'spline',
      spacing: [10, 0, 8, 0],
      height: chartStyleOverride?.chartHeight || 245,
      animation,
      backgroundColor: 'transparent',
      events: {
        load() {
          if (setHintCoords) {
            onNextTick(() => {
              // Assume that there is only one dataLabel (Hint icon dataLabel)
              const targetSeries = this.series.find(s => s.points.some(p => p.dataLabel));
              if (!targetSeries) return;
              const targetPoint = targetSeries.points.find(p => p.dataLabel);
              if (!targetPoint) return;
              // To ensure that absoluteBox is calculated
              setHintCoords(targetPoint.dataLabel.absoluteBox);
            }, 350);
          }
        },
      },
    },
    yAxis: {
      title: null,
      floor,
      ceiling,
      minPadding: 0.01,
      maxPadding: 0.01,
      startOnTick: true,
      tickAmount: 5,
      gridLineColor: '#EDF0F5',
      labels: {
        reserveSpace: true,
        style: {
          color: '#848EA1',
          fontSize: 12,
        },
        formatter() {
          return decorateYValue(this.value, metric === 'retention' ? '%' : '');
        },
      },
    },
    xAxisOptions: {
      crosshair: false,
      lineWidth: 0,
      tickLength: 0,
      type: 'category',
    },
    xAxisLabels: {
      formatter() {
        if (!showEdgeLabels || this.isFirst || this.isLast) {
          return `Week ${this.value}`;
        }
        return '';
      },
      style: {
        color: '#848EA1',
        fontSize: 12,
      },
      y: 18,
      showFirstLabel: true,
      step: labelStep,
    },
    tooltipOptions: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderRadius: 10,
      padding: 0,
      useHTML: true,
      outside: true,
      formatter() {
        let rows;

        const template = (point, index) => (
          <tr key={index}>
            <td className="text-left text-black text-14 p-l-0 p-b-0 pr-md">
              <div className="text-black text-14 d-flex" style={{ maxWidth: '100%' }}>
                <span
                  className="tooltip-bubble-marker mt-xxxs"
                  style={{
                    backgroundColor: point.color,
                    borderColor: point.color,
                  }}
                />
                <span>{point.point.tooltipPointLabel}</span>
              </div>
            </td>
            <td className="text-right text-black text-14 p-l-0 p-b-0">
              {decorateYValue(point.y, metric === 'retention' ? '%' : ' minutes')}
            </td>
          </tr>
        );

        if (Array.isArray(this.points)) {
          rows = this.points.map((point, index) => template(point, index));
        } else return false;

        const tooltipHeader = null;

        return renderToString(
          <div
            style={{
              minWidth: '189px',
              borderRadius: '8px',
              border: '1px solid #E1E5EB',
              padding: '16px',
              backgroundColor: '#fff',
              boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.08)',
            }}
          >
            {tooltipHeader && (
              <div
                style={{
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '150%',
                  color: '#848EA1',
                  marginBottom: '8px',
                }}
              >
                {tooltipHeader}
              </div>
            )}
            <table className="table">{rows}</table>
          </div>,
        );
      },
    },
    plotOptionsSeries: {
      animation,
    },
    showNoDataMessage,
  });
};
